/** Release 3 EXP-1 (lane C) — the `ImageTransform` form shared by the custom export dialog and the presets settings. */
import { Input, Label, Select } from '@/components/Input';
import { FIT_LABELS, FORMAT_LABELS, MAX_EDGE, MIN_EDGE, SCALES, type TransformForm } from './exportModel';

export function TransformFields({ value, onChange, idPrefix = 'xf' }: { value: TransformForm; onChange: (next: TransformForm) => void; idPrefix?: string }) {
  const set = <K extends keyof TransformForm>(k: K, v: TransformForm[K]) => onChange({ ...value, [k]: v });
  const lossy = value.format !== 'png' && value.format !== 'original';
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-transform-fields>
      <div>
        <Label htmlFor={`${idPrefix}-format`}>Format</Label>
        <Select id={`${idPrefix}-format`} value={value.format} onChange={(e) => set('format', e.target.value as TransformForm['format'])} data-xf="format">
          {(Object.keys(FORMAT_LABELS) as TransformForm['format'][]).map((f) => (
            <option key={f} value={f}>
              {FORMAT_LABELS[f]}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor={`${idPrefix}-fit`}>Size</Label>
        <Select id={`${idPrefix}-fit`} value={value.fit} onChange={(e) => set('fit', e.target.value as TransformForm['fit'])} data-xf="fit">
          {(Object.keys(FIT_LABELS) as TransformForm['fit'][]).map((f) => (
            <option key={f} value={f}>
              {FIT_LABELS[f]}
            </option>
          ))}
        </Select>
      </div>
      {value.fit === 'scale' && (
        <div>
          <Label htmlFor={`${idPrefix}-scale`}>Scale</Label>
          <Select id={`${idPrefix}-scale`} value={String(value.scale)} onChange={(e) => set('scale', Number(e.target.value) as TransformForm['scale'])} data-xf="scale">
            {SCALES.map((s) => (
              <option key={s} value={String(s)}>
                {Math.round(s * 100)} %
              </option>
            ))}
          </Select>
        </div>
      )}
      {value.fit === 'long_edge' && (
        <div>
          <Label htmlFor={`${idPrefix}-long-edge`}>Long edge (px)</Label>
          <Input id={`${idPrefix}-long-edge`} inputMode="numeric" min={MIN_EDGE} max={MAX_EDGE} value={value.long_edge} onChange={(e) => set('long_edge', e.target.value)} data-xf="long_edge" />
        </div>
      )}
      {value.fit === 'box' && (
        <>
          <div>
            <Label htmlFor={`${idPrefix}-width`}>Max width (px)</Label>
            <Input id={`${idPrefix}-width`} inputMode="numeric" placeholder="any" value={value.width} onChange={(e) => set('width', e.target.value)} data-xf="width" />
          </div>
          <div>
            <Label htmlFor={`${idPrefix}-height`}>Max height (px)</Label>
            <Input id={`${idPrefix}-height`} inputMode="numeric" placeholder="any" value={value.height} onChange={(e) => set('height', e.target.value)} data-xf="height" />
          </div>
        </>
      )}
      {lossy && (
        <div>
          <Label htmlFor={`${idPrefix}-quality`}>Quality (1–100)</Label>
          <Input id={`${idPrefix}-quality`} inputMode="numeric" placeholder="default" value={value.quality} onChange={(e) => set('quality', e.target.value)} data-xf="quality" />
        </div>
      )}
      <div>
        <Label htmlFor={`${idPrefix}-suffix`}>File name suffix</Label>
        <Input id={`${idPrefix}-suffix`} placeholder="_web" maxLength={24} value={value.suffix} onChange={(e) => set('suffix', e.target.value)} data-xf="suffix" />
      </div>
      <label className="flex items-center gap-2 self-end pb-2 text-sm">
        <input type="checkbox" checked={value.strip_metadata} onChange={(e) => set('strip_metadata', e.target.checked)} data-xf="strip_metadata" />
        Strip EXIF / XMP metadata
      </label>
      <p className="text-xs text-fg-subtle sm:col-span-2">Images are never upscaled beyond the original unless a long edge or box larger than it is asked for (capped at {MAX_EDGE} px).</p>
    </div>
  );
}
